import { createContext, useContext, useState, useRef, useCallback } from "react";
import { api } from "../lib/api";

const LangCtx = createContext(null);

export const LANGS = [
  { code: "en", label: "EN", full: "English", html: "en" },
  { code: "shona", label: "SN", full: "chiShona", html: "sn" },
  { code: "ndebele", label: "ND", full: "isiNdebele", html: "nd" },
];

const STORAGE_LANG = "hutano_lang";

function loadCache(lang) {
  try { return JSON.parse(localStorage.getItem(`hutano_tr_${lang}`) || "{}"); } catch { return {}; }
}
function saveCache(lang, cache) {
  try { localStorage.setItem(`hutano_tr_${lang}`, JSON.stringify(cache)); } catch { /* quota */ }
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(localStorage.getItem(STORAGE_LANG) || "en");
  const [version, setVersion] = useState(0);
  const cacheRef = useRef({});
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);

  if (!cacheRef.current[lang]) cacheRef.current[lang] = loadCache(lang);

  const flush = useCallback(async () => {
    const l = lang;
    if (l === "en") { pendingRef.current = new Set(); return; }
    const texts = Array.from(pendingRef.current);
    pendingRef.current = new Set();
    if (!texts.length) return;
    try {
      const { data } = await api.post("/ai/translate-batch", { texts, target: l });
      const c = cacheRef.current[l] || {};
      Object.assign(c, data.translations || {});
      cacheRef.current[l] = c;
      saveCache(l, c);
      setVersion((v) => v + 1);
    } catch { /* keep English fallback */ }
  }, [lang]);

  const queue = useCallback((text) => {
    pendingRef.current.add(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 250);
  }, [flush]);

  const t = useCallback((text) => {
    if (!text || typeof text !== "string" || lang === "en") return text;
    const c = cacheRef.current[lang] || {};
    if (c[text] != null) return c[text];
    queue(text);
    return text;
  }, [lang, version, queue]);

  const setLang = useCallback((l) => {
    if (!cacheRef.current[l]) cacheRef.current[l] = loadCache(l);
    localStorage.setItem(STORAGE_LANG, l);
    const meta = LANGS.find((x) => x.code === l);
    document.documentElement.lang = meta ? meta.html : "en";
    setLangState(l);
  }, []);

  return <LangCtx.Provider value={{ lang, setLang, t, version }}>{children}</LangCtx.Provider>;
}

export function useT() {
  const ctx = useContext(LangCtx);
  if (!ctx) return { t: (x) => x, lang: "en", setLang: () => {}, version: 0 };
  return ctx;
}
